// Texts and small helpers of the event page around sign-up add-ons ("Zusätze") — shared by the add form
// (pages/Event.js), the participant list (EventList) and the unregistered section (UnregisteredPlayersSection).
// Rules live in util/addOns.js (mirror of the backend); this file only words them.
import { signupLines } from '../../util/addOns';

// Sign-up add-ons of an event (EventDetailResponse.addOns with a process row), in process order.
export function eventSignupAddOns(event) {
  return (Array.isArray(event?.addOns) ? event.addOns : []).filter(a => a && a.bookableOnSignup);
}

// Add-ons bookable on site at the event ("Artikel"), live from the catalog at the event's location.
export function eventOnSiteAddOns(event) {
  return (Array.isArray(event?.addOns) ? event.addOns : []).filter(a => a && a.bookableOnSite);
}

// Signup quantity per add-on of a participant → { [addOnId]: quantity } (lines without add-on are skipped).
export function signupQuantities(participant) {
  const result = {};
  signupLines(participant).forEach(l => {
    if (l?.addOnId == null) return;
    const id = Number(l.addOnId);
    result[id] = (result[id] ?? 0) + Math.max(0, Math.trunc(Number(l.quantity) || 0));
  });
  return result;
}

// Form state { [addOnId]: quantity } → request list [{ addOnId, quantity }] (only booked add-ons).
export function toQuantityInputs(values) {
  return Object.entries(values ?? {})
    .map(([addOnId, quantity]) => ({ addOnId: Number(addOnId), quantity: Math.max(0, Math.trunc(Number(quantity) || 0)) }))
    .filter(x => x.quantity > 0);
}

const quote = (name) => `„${name ?? ''}“`;

// "„A“", "„A“ und „B“", "„A“, „B“ und „C“".
function joinNames(parts) {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

// Exhausted add-ons of a waitlist answer (AddOnCapacityInfo: { name, used, maxQuantity }):
// "„Leihausrüstung“ ist ausgebucht (8/8)" / "„Leihausrüstung“ (8/8) und „BBs“ (20/20) sind ausgebucht".
export function describeExhaustedAddOns(addOns) {
  const list = (Array.isArray(addOns) ? addOns : []).filter(Boolean);
  if (list.length === 0) return 'Ein Zusatz ist ausgebucht';
  const parts = list.map(a => {
    const max = Number(a.maxQuantity) || 0;
    return max > 0 ? `${quote(a.name)} (${Number(a.used) || 0}/${max})` : quote(a.name);
  });
  if (list.length === 1) {
    const a = list[0];
    const max = Number(a.maxQuantity) || 0;
    return `${quote(a.name)} ist ausgebucht${max > 0 ? ` (${Number(a.used) || 0}/${max})` : ''}`;
  }
  return `${joinNames(parts)} sind ausgebucht`;
}

// Reductions of an admission (AddOnReduction: { name, requested, booked }) for the success bar:
// "BBs 1 statt 2, Pyro nicht gebucht" — '' without reductions.
export function formatReductions(reductions) {
  return (Array.isArray(reductions) ? reductions : [])
    .filter(Boolean)
    .map(r => (Number(r.booked) > 0
      ? `${r.name} ${Number(r.booked)} statt ${Number(r.requested)}`
      : `${r.name} nicht gebucht`))
    .join(', ');
}

// Success text with the reductions appended: "Spieler hinzugefügt – gekürzt (ausgebucht): BBs 1 statt 2".
export function withReductions(base, reductions) {
  const text = formatReductions(reductions);
  return text ? `${base} – gekürzt (ausgebucht): ${text}` : base;
}
